import type {
  Absence, Action, EtatExportExcel, ResultatExportExcel, Affectation, Annulation, Atelier, AtelierCoanimation, Demande, DemandeEvent, ResultatCreation,
  Echeance, FakBiblioEntree, FakDetail, FakEditeur, FakFeedback, FakKit, Fusion, HistoriqueRemplacement, ListeMessagesAEnvoyer, ListePresence, Meta, MessageDemande, OutboxMessage,
  ParticipantProgramme, Personne, Pole, Recherche, RetourDemande, RetourRemplacement, Role, SeanceProgramme, User,
  AdminStats, ControleSolde, RetourAvis, StatutAvis, TypeAvis, PassationContenu, VerificationPassation,
} from '../types';

const API_BASE = '/api';

// Aperçu intervenant·e (voir RoleContext) : drapeau en mémoire, jamais persisté
// (un F5 le réinitialise naturellement). Tant qu'il est actif, chaque requête
// porte l'en-tête que le serveur utilise pour s'auto-restreindre au rôle
// intervenant (server/middleware/roleAuth.ts::rolesUtilisateur).
let rolePreviewActif = false;
export function setRolePreview(actif: boolean): void { rolePreviewActif = actif; }

/** Erreur d'API qui garde le corps de la réponse (code, données utiles à l'écran). */
export class ApiError extends Error {
  status: number;
  corps: Record<string, unknown>;
  constructor(message: string, status: number, corps: Record<string, unknown>) {
    super(message);
    this.status = status;
    this.corps = corps;
  }
}

// LE seul endroit où fetch est appelé. Les composants importent `api`.
async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(rolePreviewActif ? { 'X-Role-Preview': 'intervenant' } : {}),
      ...options.headers,
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'La requête a échoué' }));
    throw new ApiError(body.error || 'La requête a échoué', res.status, body);
  }
  return res.json();
}

const qs = (params: Record<string, string | number | boolean | undefined>) => {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== '');
  if (!entries.length) return '';
  return '?' + entries.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&');
};

export const api = {
  // NB : l'authentification (login/logout/me) passe par auth-sso-kit
  // (AuthProvider + authApi du kit), pas par ce client.

  // ── Méta (libellés statuts FR, pôles, terminaux) ─────────────────────────
  getMeta: () => request<Meta>('/meta'),

  // ── Demandes ──────────────────────────────────────────────────────────────
  /** Filtres: statut, type, pole_id, atelier_id, personne_id, du/au (date_atelier), actives=true (non terminales), miennes=true */
  getDemandes: (filtres: Record<string, string | number | boolean | undefined> = {}) =>
    request<Demande[]>(`/demandes${qs(filtres)}`),
  getDemande: (id: number) => request<Demande>(`/demandes/${id}`),
  /** Séances (atelier + date) de MA demande réservée à la gestion : jamais montrée, mais pas « à couvrir » non plus (calendrier « Mes demandes »). */
  getSeancesReservees: (filtres: { du?: string; au?: string } = {}) =>
    request<{ atelier_id: number; date_atelier: string }[]>(`/demandes/seances-reservees${qs(filtres)}`),
  /** Séances où le compte connecté est le/la remplaçant·e désigné·e (à confirmer, puis à venir). */
  getMesRemplacements: () => request<Demande[]>('/demandes/mes-remplacements'),
  /** Création (wizard) — une demande par (atelier × date), résolution par séance. */
  createDemandes: (data: {
    absent_personne_id: number;
    dates: string[];                  // dates d'absence
    ateliers: {
      atelier_id: number;
      date_atelier: string;
      resolution: 'maintenue' | 'remplacant' | 'recherche' | 'plus_tard';
      remplacant?: { personne_id: number; stagiaire: boolean; garant_personne_id?: number };
      collegues_suggeres_ids?: number[];   // ancien écran : sans effet
      /** « Prévenir aussi personnellement » : 'recherche' seulement ; internes actifs non stagiaires, 10 au plus. */
      prevenir_ids?: number[];
    }[];
    dates_flexibles?: boolean;
    notes?: string;
    tipee_rappel_vu?: boolean;
    motif_absence?: 'vacances' | 'autre';
    hors_solde_confirme?: boolean;   // a vu l'avertissement « solde insuffisant » et continue
  }) => request<ResultatCreation>('/demandes', { method: 'POST', body: JSON.stringify(data) }),
  /** Solde de vacances Tipee vs heures demandées (aucune écriture). */
  controleSolde: (absent_personne_id: number, dates: string[]) =>
    request<ControleSolde>('/demandes/controle-solde', { method: 'POST', body: JSON.stringify({ absent_personne_id, dates }) }),
  /** RH : la demande Tipee est à compléter (la demande reste en attente de Tipee). */
  tipeeACompleter: (id: number, message: string) =>
    request<Demande>(`/demandes/${id}/tipee-a-completer`, { method: 'POST', body: JSON.stringify({ message }) }),
  updateDemande: (id: number, data: Partial<Pick<Demande, 'date_atelier' | 'notes' | 'dates_flexibles' | 'collegues_suggeres' | 'tipee_rappel_vu' | 'facturation'>>) =>
    request<Demande>(`/demandes/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  /** Toute transition d'état passe par ici (machine à états côté serveur). */
  /** « Relancer X » : rappelle à la personne désignée qu'on attend sa réponse (une fois le délai écoulé). */
  relancerConfirmation: (id: number) => request<{ ok: true; remplacant_nom: string }>(`/demandes/${id}/relancer-confirmation`, { method: 'POST' }),
  appliquerAction: (id: number, action: Action, details?: Record<string, unknown>) =>
    request<Demande>(`/demandes/${id}/actions`, { method: 'POST', body: JSON.stringify({ action, details }) }),
  /** Étape 7 « Retour » : lecture, brouillon, transmission (voir server/utils/retour.ts pour qui peut quoi). */
  /** DEV seulement : saute l'étape 6 « Atelier » en ramenant la séance à hier (le serveur répond 404 hors dev). */
  /** Test (dev seulement) : le délai de réponse du/de la remplaçant·e est écoulé. */
  devDelaiConfirmation: (id: number) => request<Demande>(`/demandes/${id}/dev-delai-confirmation`, { method: 'POST' }),
  devSeancePassee: (id: number) => request<{ ok: true; date_atelier: string }>(`/demandes/${id}/dev-seance-passee`, { method: 'POST' }),
  // Contenu de la passation (DECISIONS §37)
  getPassationContenu: (id: number) => request<PassationContenu>(`/demandes/${id}/passation/contenu`),
  enregistrerPassationContenu: (id: number, corps: { logistique_texte?: string | null; remarques?: { participant_id: number; remarque?: string | null; premiere_fois?: boolean | null }[] }) =>
    request<{ ok: true }>(`/demandes/${id}/passation/contenu`, { method: 'PUT', body: JSON.stringify(corps) }),
  verifierPassation: (id: number) => request<VerificationPassation>(`/demandes/${id}/passation/verification`),
  marquerPassationLue: (id: number) => request<{ ok: true }>(`/demandes/${id}/passation/lu`, { method: 'POST' }),
  getRetour: (id: number) => request<RetourDemande>(`/demandes/${id}/retour`),
  enregistrerRetour: (id: number, data: Partial<Pick<RetourRemplacement, 'faits_marquants' | 'protocoles' | 'rien_a_signaler' | 'presence_remplie'>>) =>
    request<RetourRemplacement>(`/demandes/${id}/retour`, { method: 'PUT', body: JSON.stringify(data) }),
  transmettreRetour: (id: number, data: Partial<Pick<RetourRemplacement, 'faits_marquants' | 'protocoles' | 'rien_a_signaler' | 'presence_remplie'>>) =>
    request<RetourRemplacement>(`/demandes/${id}/retour/transmettre`, { method: 'POST', body: JSON.stringify(data) }),
  getDemandeEvents: (id: number) => request<DemandeEvent[]>(`/demandes/${id}/events`),

  // Sous-ressources d'une demande
  getRecherches: (demandeId: number) => request<Recherche[]>(`/demandes/${demandeId}/recherches`),
  ajouterReponse: (demandeId: number, rechercheId: number, data: { personne_id?: number; positive: boolean; commentaire?: string }) =>
    request<Recherche>(`/demandes/${demandeId}/recherches/${rechercheId}/reponses`, { method: 'POST', body: JSON.stringify(data) }),
  getAffectation: (demandeId: number) => request<Affectation | null>(`/demandes/${demandeId}/affectation`),
  setAffectation: (demandeId: number, data: { remplacant_personne_id: number; stagiaire: boolean; garant_personne_id?: number }) =>
    request<Affectation>(`/demandes/${demandeId}/affectation`, { method: 'PUT', body: JSON.stringify(data) }),
  getPassation: (demandeId: number) => request<import('../types').Passation | null>(`/demandes/${demandeId}/passation`),
  updatePassation: (demandeId: number, data: Record<string, unknown>) =>
    request<import('../types').Passation>(`/demandes/${demandeId}/passation`, { method: 'PATCH', body: JSON.stringify(data) }),
  getFusion: (demandeId: number) => request<Fusion | null>(`/demandes/${demandeId}/fusion`),
  updateFusion: (demandeId: number, data: Partial<Fusion>) =>
    request<Fusion>(`/demandes/${demandeId}/fusion`, { method: 'PATCH', body: JSON.stringify(data) }),
  getAnnulation: (demandeId: number) => request<Annulation | null>(`/demandes/${demandeId}/annulation`),
  updateAnnulation: (demandeId: number, data: Partial<Annulation>) =>
    request<Annulation>(`/demandes/${demandeId}/annulation`, { method: 'PATCH', body: JSON.stringify(data) }),
  getPresences: (demandeId: number) => request<ListePresence | null>(`/demandes/${demandeId}/presences`),
  updatePresences: (demandeId: number, data: Partial<ListePresence>) =>
    request<ListePresence>(`/demandes/${demandeId}/presences`, { method: 'PATCH', body: JSON.stringify(data) }),
  /** Urgence: mise à jour d'une étape de cascade (statut/commentaire). */
  /** `version` = la date de dernière modification vue par l'écran (refusé si quelqu'un a modifié l'urgence depuis) ; `resume` = la ligne du journal. */
  updateCascade: (demandeId: number, etapes: import('../types').CascadeEtape[], opts: { version?: string; resume?: string } = {}) =>
    request<Demande>(`/demandes/${demandeId}/cascade`, { method: 'PATCH', body: JSON.stringify({ etapes, version: opts.version, resume: opts.resume }) }),
  /** Les contacts à appeler (avec leur disponibilité) et le journal d'une urgence. */
  getContexteUrgence: (demandeId: number) => request<import('../types').ContexteUrgence>(`/demandes/${demandeId}/urgence-contexte`),
  /** Urgence: création directe (intake téléphone) → statut urgence_cascade. */
  createUrgence: (data: {
    absent_personne_id: number;
    atelier_id: number;
    date_atelier: string;
    type: 'urgence_weekend' | 'urgence_semaine';
    notes?: string;
  }) => request<Demande>('/demandes/urgence', { method: 'POST', body: JSON.stringify(data) }),
  /** Écarte (→ sans_suite) les brouillons dont la séance est déjà passée. */
  nettoyerBrouillonsPerimes: () =>
    request<{ ok: true; ecartees: number; total: number }>('/demandes/nettoyer-brouillons-perimes', { method: 'POST' }),
  /** Applique la même action à plusieurs demandes (sélection du Tableau de bord). */
  appliquerActionGroupee: (ids: number[], action: Action) =>
    request<{ ok: true; reussies: number; echouees: { id: number; erreur: string }[] }>(
      '/demandes/actions-groupees', { method: 'POST', body: JSON.stringify({ ids, action }) },
    ),
  /** Suppression (corbeille) en masse — réversible via restaurerDemandes. */
  supprimerDemandes: (ids: number[]) =>
    request<{ ok: true; reussies: number; echouees: { id: number; erreur: string }[] }>(
      '/demandes/supprimer-groupe', { method: 'POST', body: JSON.stringify({ ids }) },
    ),
  restaurerDemandes: (ids: number[]) =>
    request<{ ok: true; reussies: number; echouees: { id: number; erreur: string }[] }>(
      '/demandes/restaurer-groupe', { method: 'POST', body: JSON.stringify({ ids }) },
    ),
  /** Onglet « Demandes des collègues » (vue calendrier) : ignorer/réafficher — préférence d'affichage personnelle, jamais une action sur la demande. */
  ignorerDemande: (id: number) => request<{ ok: true }>(`/demandes/${id}/skip`, { method: 'POST' }),
  reafficherDemande: (id: number) => request<{ ok: true }>(`/demandes/${id}/skip`, { method: 'DELETE' }),
  getDemandesIgnorees: () => request<Demande[]>('/demandes/skips/mine'),

  // ── Personnes (annuaire + vivier externe) ────────────────────────────────
  getPersonnes: (filtres: { externe?: boolean; actif?: boolean; pole_id?: number; q?: string } = {}) =>
    request<Personne[]>(`/personnes${qs(filtres)}`),
  getPersonne: (id: number) => request<Personne>(`/personnes/${id}`),
  /** La fiche du compte connecté (null si le compte n'est relié à aucune fiche) — le serveur est la référence. */
  getMaPersonne: () => request<Personne | null>('/personnes/moi'),
  createPersonne: (data: Partial<Personne>) =>
    request<Personne>('/personnes', { method: 'POST', body: JSON.stringify(data) }),
  updatePersonne: (id: number, data: Partial<Personne>) =>
    request<Personne>(`/personnes/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  /** Historique de remplacement (nb clôturés) pour classer les collègues suggéré·e·s. */
  getHistoriqueRemplacements: (atelierIds: number[]) =>
    request<HistoriqueRemplacement[]>(`/personnes/historique-remplacements?atelier_ids=${atelierIds.join(',')}`),

  // ── Ateliers & FRA ────────────────────────────────────────────────────────
  getAteliers: (filtres: { actif?: boolean; pole_id?: number; reel?: boolean } = {}) =>
    request<Atelier[]>(`/ateliers${qs(filtres)}`),
  getAtelier: (id: number) => request<Atelier>(`/ateliers/${id}`),
  /** Administration › Co-animation : ateliers actifs à deux animateur·rice·s (DECISIONS §36). */
  getAteliersCoanimation: () => request<AtelierCoanimation[]>('/ateliers/coanimation'),
  updateAtelier: (id: number, data: Partial<Atelier>) =>
    request<Atelier>(`/ateliers/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  getFaks: (filtres: { statut?: string } = {}) => request<FakKit[]>(`/fak${qs(filtres)}`),
  getFakBibliotheque: () => request<FakBiblioEntree[]>('/fak/bibliotheque'),
  getFak: (atelierId: number) => request<FakDetail>(`/fak/atelier/${atelierId}`),
  upsertFak: (atelierId: number, data: Partial<FakKit>) =>
    request<FakKit>(`/fak/atelier/${atelierId}`, { method: 'PUT', body: JSON.stringify(data) }),
  confirmerRevueFak: (atelierId: number) =>
    request<FakKit>(`/fak/atelier/${atelierId}/confirmer-revue`, { method: 'POST' }),
  getFakFeedback: (fakId: number) => request<FakFeedback[]>(`/fak/${fakId}/feedback`),
  addFakFeedback: (fakId: number, data: { contenu: string; demande_id?: number }) =>
    request<FakFeedback>(`/fak/${fakId}/feedback`, { method: 'POST', body: JSON.stringify(data) }),
  setFeedbackIntegre: (fakId: number, feedbackId: number, integre: boolean) =>
    request<FakFeedback>(`/fak/${fakId}/feedback/${feedbackId}`, { method: 'PATCH', body: JSON.stringify({ integre }) }),
  /** Éditeurs FRA délégués (en plus de la gestion et du/de la titulaire) — gérés par utils/fakAccess.ts::canManageFakEditeurs. */
  getFakEditeurs: (atelierId: number) => request<FakEditeur[]>(`/fak/atelier/${atelierId}/editeurs`),
  ajouterFakEditeur: (atelierId: number, personneId: number) =>
    request<{ ok: true }>(`/fak/atelier/${atelierId}/editeurs`, { method: 'POST', body: JSON.stringify({ personne_id: personneId }) }),
  retirerFakEditeur: (atelierId: number, personneId: number) =>
    request<{ ok: true }>(`/fak/atelier/${atelierId}/editeurs/${personneId}`, { method: 'DELETE' }),

  // ── Absences (miroir Tipee + manuelles) ──────────────────────────────────
  getAbsences: (filtres: { du?: string; au?: string; personne_id?: number } = {}) =>
    request<Absence[]>(`/absences${qs(filtres)}`),
  createAbsence: (data: { personne_id: number; date_absence: string; type_absence?: string; remarque?: string }) =>
    request<Absence>('/absences', { method: 'POST', body: JSON.stringify(data) }),
  /** Déclenche une synchro Tipee immédiate (stub ou live selon settings). */
  syncTipee: () => request<{ ok: true; upserts: number; brouillons_crees: number; tipee_confirmees: number }>('/absences/sync-tipee', { method: 'POST' }),

  // ── Programme (stub DB participants) ─────────────────────────────────────
  getSeances: (filtres: { du?: string; au?: string; atelier_id?: number } = {}) =>
    request<SeanceProgramme[]>(`/programme/seances${qs(filtres)}`),
  getParticipants: (atelierId: number, date?: string) =>
    request<ParticipantProgramme[]>(`/programme/ateliers/${atelierId}/participants${qs({ date })}`),

  // ── Outbox & échéances ────────────────────────────────────────────────────
  getOutbox: (filtres: { statut?: string; canal?: string } = {}) =>
    request<OutboxMessage[]>(`/outbox${qs(filtres)}`),
  marquerEnvoye: (id: number) =>
    request<OutboxMessage>(`/outbox/${id}/envoye`, { method: 'POST' }),
  // Messages à envoyer (Slack / e-mail / SMS) — docs/DECISIONS.md §24
  getMessagesDemande: (demandeId: number) =>
    request<MessageDemande[]>(`/messages/demande/${demandeId}`),
  getMessagesAEnvoyer: () =>
    request<ListeMessagesAEnvoyer>('/messages/a-envoyer'),
  getCompteMessages: () =>
    request<{ a_envoyer: number }>('/messages/compte'),
  marquerMessageEnvoye: (id: number) =>
    request<MessageDemande>(`/messages/${id}/envoye`, { method: 'POST' }),
  marquerSmsEnvoyes: (demandeId: number) =>
    request<{ marques: number }>(`/messages/demande/${demandeId}/sms-envoyes`, { method: 'POST' }),
  getEcheances: (filtres: { a_venir?: boolean } = {}) =>
    request<Echeance[]>(`/echeances${qs(filtres)}`),

  // ── Réglages & administration ────────────────────────────────────────────
  getSettings: () => request<Record<string, string>>('/settings'),
  updateSetting: (key: string, value: string) =>
    request<{ ok: true }>(`/settings/${encodeURIComponent(key)}`, { method: 'PUT', body: JSON.stringify({ value }) }),
  getUsers: () => request<User[]>('/users'),
  createUser: (data: { name: string; email: string; password: string; role: Role; personne_id?: number }) =>
    request<User>('/users', { method: 'POST', body: JSON.stringify(data) }),
  updateUser: (id: number, data: Partial<{ name: string; email: string; role: Role; personne_id: number | null; password: string }>) =>
    request<User>(`/users/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  syncEveil: () => request<{ ok: true; rapport: Record<string, number> }>('/administration/sync-eveil', { method: 'POST' }),
  getAdminStats: () => request<AdminStats>('/administration/stats'),
  testSlack: () => request<{ ok: true }>('/settings/test-slack', { method: 'POST' }),
  testChatbot: () => request<{ ok: true; reponse: string }>('/settings/test-chatbot', { method: 'POST' }),
  getEtatExportExcel: () => request<EtatExportExcel>('/settings/export-excel'),
  envoyerExportExcel: () => request<ResultatExportExcel>('/settings/export-excel', { method: 'POST' }),

  // ── Votre avis ────────────────────────────────────────────────────────────
  envoyerAvis: (data: {
    message: string; type: TypeAvis | null; page: string; lien: string;
    element: { libelle: string; chemin: string; position: { x: number; y: number; w: number; h: number } } | null;
    navigateur: string; fenetre: string; suivi: boolean;
  }) => request<{ id: number }>('/avis', { method: 'POST', body: JSON.stringify(data) }),
  getAvis: (statut?: StatutAvis) => request<RetourAvis[]>(`/avis${qs({ statut })}`),
  changerStatutAvis: (id: number, statut: StatutAvis) =>
    request<{ id: number; statut: StatutAvis }>(`/avis/${id}/statut`, { method: 'PUT', body: JSON.stringify({ statut }) }),

  // ── Assistant (chatbox) ───────────────────────────────────────────────────
  sendChatMessage: (messages: { role: 'user' | 'assistant'; content: string }[]) =>
    request<{ reponse: string }>('/chatbot/message', { method: 'POST', body: JSON.stringify({ messages }) }),
};
